import React, { useState, useEffect, useCallback } from 'react';
import { Box, TextField } from '@common-ui/ui';
import { useAppDispatch } from '../../../store/configureStore';
import { storeReportInfo, storeCompanyLogo } from '../../../store/slices/reportSlice';
import reportCoverPageEnum from '../../../enums/reportCoverPageEnum';
import { AvatarOrgImage as CompanyLogo } from '../ReportCompanyLogo';
import DatePicker from '../../../components/InputFields/DatePicker';

const MAX_REPORT_NAME_LENGTH = 22;
const MAX_DESCRIPTION_LENGTH = 1500;

const DEFAULT_FORM_VALUES = {
  data: {
    company_logo: null,
    company: '',
    site_id: '',
    report_name: '',
    performed_on: '',
    created_on: '',
    description: '',
  },
  options: {
    finalized: false,
  },
};

export const ReportCoverPage = ({ reportInfo }) => {
  const dispatch = useAppDispatch();

  const [form, setForm] = useState(DEFAULT_FORM_VALUES);
  const [avatarSrc, setAvatarSrc] = useState('');
  const [isCompanyLogoBusy, setCompanyLogoBusy] = useState(true);
  const [descriptionLength, setDescriptionLength] = useState(0);

  const handleChange = useCallback(
    (event) => {
      const { name, value } = event.target;

      setForm((prevState) => ({
        ...prevState,
        data: {
          ...prevState?.data,
          [name]: value,
        },
      }));
    },
    [setForm],
  );

  const handleBlur = useCallback(() => {
    // illegal keys are purged by reportAPI
    dispatch(storeReportInfo(form));
  }, [dispatch, form]);

  const handleDescriptionChange = useCallback(
    (event) => {
      const currentLength = event.target?.value?.length ?? 0;
      setDescriptionLength(currentLength);
      handleChange(event);
    },
    [handleChange],
  );

  const displayLoadingWhileBusy = async (asyncActionFunc) => {
    setCompanyLogoBusy(true);
    if (asyncActionFunc) {
      await asyncActionFunc();
    }
    setCompanyLogoBusy(false);
  };

  const handleAvatarUpdate = useCallback(
    (image_blob = null) => {
      const asyncActionFunc = async () => {
        let newAvatarSrc = '';
        if (image_blob) {
          newAvatarSrc = URL.createObjectURL(image_blob);
        }
        setAvatarSrc(newAvatarSrc || '');

        const report_id = reportInfo?.options?.id;
        if (report_id != null) {
          await dispatch(storeCompanyLogo(report_id, image_blob));
        }
      };

      displayLoadingWhileBusy(asyncActionFunc);
    },
    [dispatch, reportInfo],
  );

  useEffect(() => {
    if (reportInfo) {
      setForm(reportInfo);
      setDescriptionLength(reportInfo?.data?.description?.length ?? 0);
    }
  }, [reportInfo]);

  useEffect(() => {
    if (form != null) {
      // create image blob
      const company_logo = form?.company_logo;

      if (company_logo != null) {
        setAvatarSrc(company_logo);
      }

      setCompanyLogoBusy(false);
    }
  }, [form]);

  return (
    <Box
      id="reportCoverPage"
      p={4}
      backgroundColor="background.paper"
      display="grid"
      gridTemplateColumns="repeat(12, 1fr)"
      sx={{ gap: 2 }}
    >
      <Box gridColumn="span 1">
        <CompanyLogo
          src={avatarSrc}
          onChange={handleAvatarUpdate}
          isLoading={isCompanyLogoBusy}
          disabled={form?.options?.finalized}
        />
      </Box>

      <Box gridColumn="span 11" display="grid" sx={{ gap: 2 }}>
        <TextField
          id="company"
          label={reportCoverPageEnum.COMPANY.LABEL}
          name={reportCoverPageEnum.COMPANY.NAME}
          value={form?.data?.company ?? ' '}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={form?.options?.finalized}
          fullWidth
        />
        <TextField
          id="location"
          label={reportCoverPageEnum.LOCATION.LABEL}
          name={reportCoverPageEnum.LOCATION.NAME}
          value={form?.data?.site_id ?? ' '}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={form?.options?.finalized}
          fullWidth
        />
      </Box>
      <Box gridColumn="span 12" display="grid" sx={{ gap: 2 }}>
        <TextField
          id="report_name"
          label={reportCoverPageEnum.REPORT_NAME.LABEL}
          name={reportCoverPageEnum.REPORT_NAME.NAME}
          value={form?.data?.report_name ?? ' '}
          inputProps={{ maxLength: MAX_REPORT_NAME_LENGTH }}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={form?.options?.finalized}
          fullWidth
        />
      </Box>
      <Box gridColumn="span 4" display="grid" sx={{ gap: 2 }}>
        <TextField
          id="created_by"
          label={reportCoverPageEnum.CREATED_BY.LABEL}
          name={reportCoverPageEnum.CREATED_BY.NAME}
          value={form?.data?.created_by ?? ' '}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={form?.options?.finalized}
          fullWidth
        />
        <TextField
          id="performed_by"
          label={reportCoverPageEnum.PERFORMED_BY.LABEL}
          name={reportCoverPageEnum.PERFORMED_BY.NAME}
          value={form?.data?.performed_by ?? ' '}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={form?.options?.finalized}
          fullWidth
        />
      </Box>
      <Box gridColumn="span 4" display="grid" sx={{ gap: 2 }}>
        <DatePicker
          id="created_on"
          label={reportCoverPageEnum.CREATED_ON.LABEL}
          name={reportCoverPageEnum.CREATED_ON.NAME}
          value={form?.data?.created_on}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={form?.options?.finalized}
        />
        <DatePicker
          id="performed_on"
          label={reportCoverPageEnum.PERFORMED_ON.LABEL}
          name={reportCoverPageEnum.PERFORMED_ON.NAME}
          value={form?.data?.performed_on}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={form?.options?.finalized}
        />
      </Box>
      <Box gridColumn="span 12">
        <TextField
          id="description"
          label={reportCoverPageEnum.DESCRIPTION.LABEL}
          name={reportCoverPageEnum.DESCRIPTION.NAME}
          value={form?.data?.description ?? ' '}
          helperText={`${descriptionLength}/${MAX_DESCRIPTION_LENGTH}`}
          inputProps={{ maxLength: MAX_DESCRIPTION_LENGTH }}
          onChange={handleDescriptionChange}
          onBlur={handleBlur}
          disabled={form?.options?.finalized}
          fullWidth
          multiline={true}
          rows={17}
        />
      </Box>
    </Box>
  );
};

export default ReportCoverPage;
